import { Skeleton, Stack } from '@mui/material';
import { EntityBase } from '@provision/entity-types';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  memo,
  MouseEvent,
  ReactElement,
  UIEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { CustomViewAttribute } from 'src/graphql/entities/custom-view';
import { CustomAttributeColumnRenderer } from 'src/graphql/entities/custom-view/custom-view-config/custom-attributes-bridge';
import type { TableSelectionState } from 'src/pages/CustomViews/custom-views-entities-table/custom-views-entities-table';
import {
  RecordClickHandler,
  useDebouncedRecordClick,
} from 'src/pages/CustomViews/hooks/use-debounced-record-click';
import { useScrollFallbackWhenNotOverflowing } from 'src/utils/hooks';
import { CARD_LAYOUT_BY_ENTITY, CardLayoutEntity, CardLayoutKind } from './mobile-card-layout';
import {
  CARD_SLOT_SIZE,
  CardListScroll,
  EmptyState,
  SkeletonCard,
  VirtualRow,
  VirtualRows,
} from './mobile-card-list.elements';
import { MobileRecordCard } from './mobile-record-card';

const INITIAL_SKELETON_COUNT = 12;
const LOAD_MORE_SKELETON_COUNT = 6;
const ESTIMATED_CARD_HEIGHT_PX_BY_KIND: Record<CardLayoutKind, number> = {
  [CardLayoutKind.ChipLine]: 68,
  [CardLayoutKind.TwoColumn]: 56,
};
const VIRTUAL_OVERSCAN = 6;
const CLICK_AFTER_OUTSIDE_CLOSE_MS = 400;
const NO_COLUMNS: CustomViewAttribute[] = [];

const ChipLineSkeleton = () => (
  <>
    <Skeleton variant="circular" width={CARD_SLOT_SIZE} height={CARD_SLOT_SIZE} />
    <Stack flex={1} minWidth={0} gap={0.5}>
      <Stack direction="row" justifyContent="space-between" gap={1.5}>
        <Skeleton variant="text" width="60%" />
        <Skeleton variant="text" width="2.25rem" />
      </Stack>
      <Skeleton variant="rounded" width="70%" height="1.0625rem" />
    </Stack>
  </>
);

const TwoColumnSkeleton = () => (
  <>
    <Stack flex={1} minWidth={0}>
      <Skeleton variant="text" width="55%" />
      <Skeleton variant="text" width="75%" />
    </Stack>
    <Stack alignItems="flex-end">
      <Skeleton variant="text" width="3.5rem" />
      <Skeleton variant="text" width="2.25rem" />
    </Stack>
  </>
);

const SkeletonCards = ({ count, kind }: { count: number; kind: CardLayoutKind }) => (
  <>
    {Array.from({ length: count }, (_, index) => (
      <SkeletonCard key={index}>
        {kind === CardLayoutKind.TwoColumn ? <TwoColumnSkeleton /> : <ChipLineSkeleton />}
      </SkeletonCard>
    ))}
  </>
);

const CustomViewsMobileCardListComp = <T extends EntityBase>({
  rows,
  entityName,
  selectedColumns,
  allAttributes,
  customAttributeRenderers,
  onClick,
  selectedId,
  onScroll,
  isLoading,
  isLoadingMore,
  selectionState,
}: {
  rows: T[];
  entityName: CardLayoutEntity;
  selectedColumns?: CustomViewAttribute[] | null;
  allAttributes?: CustomViewAttribute[];
  customAttributeRenderers?: Map<string, CustomAttributeColumnRenderer>;
  onClick?: RecordClickHandler<T>;
  selectedId?: string;
  onScroll?: (e: UIEvent) => unknown;
  isLoading?: boolean;
  isLoadingMore?: boolean;
  selectionState?: TableSelectionState;
}) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const columns = selectedColumns ?? NO_COLUMNS;
  const isSelectionMode = !!selectionState?.isSomeVisibleSelected;

  const [openSwipeId, setOpenSwipeId] = useState<string | null>(null);
  // a timestamp read by the next click only; storing it in state would re-render the list for nothing
  const swallowClicksUntilRef = useRef(0);

  const handleSwipeOpenChange = useCallback((id: string, isOpen: boolean) => {
    setOpenSwipeId((openId) => {
      if (isOpen) return id;
      return openId === id ? null : openId;
    });
  }, []);

  const handleSwipeStart = useCallback((id: string) => {
    setOpenSwipeId((openId) => (openId === id ? openId : null));
  }, []);

  // one card is open at a time, and a tap anywhere outside it only closes it
  useEffect(() => {
    if (!openSwipeId) return undefined;

    const handlePointerDown = (event: PointerEvent) => {
      const openRow = scrollRef.current?.querySelector(
        `[data-swipe-row="${CSS.escape(openSwipeId)}"]`,
      );
      if (openRow?.contains(event.target as Node)) return;

      swallowClicksUntilRef.current = Date.now() + CLICK_AFTER_OUTSIDE_CLOSE_MS;
      setOpenSwipeId(null);
    };

    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => document.removeEventListener('pointerdown', handlePointerDown, true);
  }, [openSwipeId]);

  const handleClickCapture = (event: MouseEvent<HTMLDivElement>) => {
    if (Date.now() >= swallowClicksUntilRef.current) return;

    event.preventDefault();
    event.stopPropagation();
  };

  useScrollFallbackWhenNotOverflowing({
    containerRef: scrollRef,
    onScroll,
    dataKey: rows.length,
  });

  const { kind } = CARD_LAYOUT_BY_ENTITY[entityName];
  const estimateCardHeight = useCallback(() => ESTIMATED_CARD_HEIGHT_PX_BY_KIND[kind], [kind]);

  const debouncedOnClick = useDebouncedRecordClick<T>(onClick);

  const getItemKey = useCallback((index: number) => String(rows[index].id), [rows]);

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: estimateCardHeight,
    overscan: VIRTUAL_OVERSCAN,
    getItemKey,
  });

  const showInitialSkeleton = !!isLoading && rows.length === 0;
  const showEmptyState = !isLoading && rows.length === 0;

  return (
    <CardListScroll
      ref={scrollRef}
      onScroll={onScroll}
      onClickCapture={handleClickCapture}
      data-testid="custom-views-mobile-cards"
    >
      {showInitialSkeleton ? (
        <SkeletonCards count={INITIAL_SKELETON_COUNT} kind={kind} />
      ) : null}
      <VirtualRows style={{ height: virtualizer.getTotalSize() }}>
        {virtualizer.getVirtualItems().map((virtualRow) => {
          const record = rows[virtualRow.index];

          return (
            <VirtualRow
              key={virtualRow.key}
              data-index={virtualRow.index}
              ref={virtualizer.measureElement}
              style={{ transform: `translateY(${virtualRow.start}px)` }}
            >
              <MobileRecordCard<T>
                record={record}
                entityName={entityName}
                columns={columns}
                allAttributes={allAttributes}
                isActive={!!selectedId && record.id === selectedId}
                isSelectionMode={isSelectionMode}
                selectionState={selectionState}
                customAttributeRenderers={customAttributeRenderers}
                onClick={debouncedOnClick}
                isSwipeOpen={openSwipeId === String(record.id)}
                onSwipeOpenChange={handleSwipeOpenChange}
                onSwipeStart={handleSwipeStart}
              />
            </VirtualRow>
          );
        })}
      </VirtualRows>
      {isLoadingMore ? <SkeletonCards count={LOAD_MORE_SKELETON_COUNT} kind={kind} /> : null}
      {showEmptyState ? <EmptyState variant="body2">No records to display</EmptyState> : null}
    </CardListScroll>
  );
};

export const CustomViewsMobileCardList = memo(CustomViewsMobileCardListComp) as <
  T extends EntityBase,
>(
  props: React.ComponentProps<typeof CustomViewsMobileCardListComp<T>>,
) => ReactElement;
